import React from 'react';
import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import { useQuery } from '@apollo/react-hooks';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';

export const AI_REVIEW_PROMPT_TEXT_QUERY = gql`
  query aiReviewPromptText($version: Int!) {
    aiReviewPromptVersion(version: $version) {
      version
      text
      createdAt
      createdBy
      note
    }
  }
`;

export const AI_REVIEW_PROMPT_VERSIONS_QUERY = gql`
  query aiReviewPromptVersions {
    aiReviewPromptVersions {
      version
      createdAt
      createdBy
      note
      active
    }
  }
`;

// Where the versions of a prompt come from: the AI review prompt by default, or another feature's
// prompt with its own queries (both return the AiPromptVersion fields).
export interface IPromptVersionSource {
  // "AI review prompt"
  title: string;
  textQuery: DocumentNode;
  textField: string;
  versionsQuery: DocumentNode;
  versionsField: string;
  // Sent with both queries, e.g. the prompt of a feature with several.
  variables?: Record<string, unknown>;
}

export const AI_REVIEW_PROMPT_SOURCE: IPromptVersionSource = {
  title: 'AI review prompt',
  textQuery: AI_REVIEW_PROMPT_TEXT_QUERY,
  textField: 'aiReviewPromptVersion',
  versionsQuery: AI_REVIEW_PROMPT_VERSIONS_QUERY,
  versionsField: 'aiReviewPromptVersions',
};

interface IAiPromptVersionSummary {
  version: number;
  createdAt: string;
  createdBy: string;
  note: string | null;
  active: boolean;
}

interface IAiPromptVersion {
  version: number;
  text: string;
  createdAt: string;
  createdBy: string;
  note: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    text: {
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      fontFamily: 'monospace',
      fontSize: '0.8rem',
      margin: 0,
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
    },
    compare: {
      maxWidth: 420,
      marginBottom: theme.spacing(2),
    },
    diff: {
      // The prompt lines are long sentences; wrap them instead of scrolling sideways.
      '& pre': {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
    },
  })
);

const usePromptText = (version: number | null, source: IPromptVersionSource) => {
  const result = useQuery<Record<string, IAiPromptVersion | null>>(source.textQuery, {
    variables: { ...source.variables, version },
    skip: version === null,
    fetchPolicy: 'cache-first',
  });
  return { ...result, prompt: result.data?.[source.textField] ?? null };
};

const versionLabel = (version: IAiPromptVersionSummary) =>
  `v${version.version}${version.active ? ' (active)' : ''} · ${new Date(version.createdAt).toLocaleString('sv-SE')} · ${version.createdBy}`;

const PromptVersionDialog = ({ version, source, onClose }: { version: number; source: IPromptVersionSource; onClose: () => void }) => {
  const classes = useStyles();
  const { prompt, loading, error } = usePromptText(version, source);
  // No comparison until one is chosen.
  const [compareWith, setCompareWith] = React.useState('');
  const versions = useQuery<Record<string, IAiPromptVersionSummary[]>>(source.versionsQuery, {
    variables: source.variables,
    fetchPolicy: 'cache-first',
  });
  const others = (versions.data?.[source.versionsField] || []).filter(item => item.version !== version);
  const compared = usePromptText(compareWith === '' ? null : Number(compareWith), source);
  const base = compared.prompt;
  const [copied, setCopied] = React.useState(false);
  const copy = () =>
    prompt &&
    navigator.clipboard?.writeText(prompt.text).then(
      () => setCopied(true),
      () => setCopied(false)
    );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="ai-prompt-version-title">
      <DialogTitle id="ai-prompt-version-title">
        {source.title}, version {version}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {!loading && !error && !prompt && <Alert severity="info">This prompt version was not found.</Alert>}
        {prompt && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                {version === 0 ? 'Snapshot of the prompt in code · ' : ''}
                {new Date(prompt.createdAt).toLocaleString('sv-SE')} · {prompt.createdBy} ·{' '}
                {prompt.text.length.toLocaleString('sv-SE')} characters
              </Typography>
              {prompt.note && <Typography variant="body2">What changed: {prompt.note}</Typography>}
            </Box>
            {others.length > 0 && (
              <TextField
                id="ai-prompt-version-compare"
                className={classes.compare}
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Compare with"
                value={compareWith}
                onChange={event => setCompareWith(event.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">No comparison</option>
                {others.map(item => (
                  <option key={item.version} value={String(item.version)}>
                    {versionLabel(item)}
                  </option>
                ))}
              </TextField>
            )}
            {compareWith === '' ? (
              <pre className={classes.text} data-testid="ai-prompt-version-text">
                {prompt.text}
              </pre>
            ) : compared.loading || !base ? (
              compared.error ? <Alert severity="error">{compared.error.message}</Alert> : <CircularProgress />
            ) : base.text === prompt.text ? (
              <Typography variant="body2" color="textSecondary" data-testid="ai-prompt-version-same">
                Version {base.version} and version {version} are the same.
              </Typography>
            ) : (
              <Box className={classes.diff} data-testid="ai-prompt-version-diff">
                <ReactDiffViewer
                  oldValue={base.text}
                  newValue={prompt.text}
                  splitView
                  compareMethod={DiffMethod.WORDS}
                  extraLinesSurroundingDiff={3}
                  leftTitle={`Version ${base.version}`}
                  rightTitle={`Version ${version} (opened)`}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={copy} disabled={!prompt}>
          {copied ? 'Copied' : 'Copy text'}
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface IAiPromptVersionLinkProps {
  version: number;
  // The AI review prompt when not given.
  source?: IPromptVersionSource;
  children?: React.ReactNode;
}

// Superuser only: a prompt version as a link that shows its text in a dialog.
const AiPromptVersionLink = ({ version, source = AI_REVIEW_PROMPT_SOURCE, children }: IAiPromptVersionLinkProps) => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Link
        component="button"
        type="button"
        variant="inherit"
        onClick={(event: React.MouseEvent) => {
          // Rows that open something themselves (a run) must not get the click too.
          event.stopPropagation();
          setOpen(true);
        }}
        title="Show the text of this prompt version"
      >
        {children ?? `v${version}`}
      </Link>
      {open && (
        // The dialog is a portal, but React still bubbles its clicks to the row the link sits in.
        <span onClick={event => event.stopPropagation()}>
          <PromptVersionDialog version={version} source={source} onClose={() => setOpen(false)} />
        </span>
      )}
    </>
  );
};

export default AiPromptVersionLink;
